import React, { useEffect, useState } from "react";
import {
  DELETE_USER,
  USER_REGISTER_URL,
  USERS_URL,
  REGIONS_URL,
  GET_CATEGORIES_URL,
  GET_USER_BY_ROLE,
  UPDATE_USER,
} from "../../../API/APIRoutes";
import GenericItemsListing from "../ListingItems/ListingItems";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import { Grid, TextField, Typography, Chip, Box } from "@mui/material";
import DropDown from "@Components/DropDown/DropDown";
import Joi from "joi";
import {
  DropDownValidation,
  EmailValidation,
  NameValidation,
  OptionalDropDownValidation,
  WithoutValidation,
} from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import {
  submitFormWithValidation,
  validateField,
} from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import SearchableDropDown from "../../../Components/DropDown/SearchableDropDown";
import { red } from "@mui/material/colors";
import CancelIcon from "@mui/icons-material/Cancel";
import useSendGetRequest from "@Hooks/useSendGetRequest";
import { useDispatch } from "react-redux";
import { setSnackbar } from "@Redux/snackBarSlice";
import { RegionFilter } from "@Components/RegionFilter";

function UsersListing() {
  const APPROVER_NAME = "APPROVER";
  const RD_MANAGER_NAME = "RD_MANAGER";
  const REGULATORY_NAME = "REGULATORY";
  const attributes = [
    { key: "username", label: "User Name", flex: 2 },
    { key: "email", label: "Email", flex: 2.75 },
    { key: "role", label: "Role", flex: 1.75 },
    { key: "managerEmail", label: "Manager", flex: 2 },
  ];

  const validationSchema = Joi.object({
    username: NameValidation("User Name"),
    email: EmailValidation,
    roles: DropDownValidation("Role"),
    regionId: OptionalDropDownValidation("Region Name"),
    categoryIds: WithoutValidation,
    managerId: WithoutValidation,
    regionIds: WithoutValidation
  });

  // Create dynamic validation schema based on role
  const getDynamicValidationSchema = (userRole) => {
    if (userRole === "APPROVER" || userRole === "RD_MANAGER") {
      // For approvers/RD managers, regionId is not required since they use regionIds
      return Joi.object({
        username: NameValidation("User Name"),
        email: EmailValidation,
        roles: DropDownValidation("Role"),
        regionId: WithoutValidation, // Not required for managers
        categoryIds: WithoutValidation,
        managerId: WithoutValidation,
        regionIds: WithoutValidation
      });
    } else if (userRole === "RD_COORDINATOR") {
      // RD Coordinator is system-wide — no region or category required
      return Joi.object({
        username: NameValidation("User Name"),
        email: EmailValidation,
        roles: DropDownValidation("Role"),
        regionId: WithoutValidation,
        categoryIds: WithoutValidation,
        managerId: WithoutValidation,
        regionIds: WithoutValidation
      });
    } else {
      // For non-managers, use the original schema
      return validationSchema;
    }
  };

  const defaultOption = {
    id: "Select Category Name",
    name: "Select Category Name",
  };

  const defaultRegionOption = {
    id: "Select Region Name",
    name: "Select Region Name",
  };

  const [categoryNameOptions, setCategoryNameOptions] = useState([]);
  const [managerNameOptions, setManagerNameOptions] = useState([]);

  const initialState = {
    username: "",
    email: "",
    roles: "Select Role",
    regionId: "",
    managerId: null,
  };

  const [newUserData, setnewUserData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [userCategories, setUserCategory] = useState([]);
  const [userRegions, setUserRegions] = useState([]);
  const [regionOptions, setRegionOptions] = useState([]);
  const [regionNameOptions, setRegionNameOptions] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedRegionFilter, setSelectedRegionFilter] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const { getData: getData } = useSendGetRequest();
  const [initialCategoriesLoaded, setInitialCategoriesLoaded] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [allRegions, setAllRegions] = useState([]);
  const [formKey, setFormKey] = useState(0); // Add a key to force form re-render
  const dispatch = useDispatch();

  // Check for success message after page reload
  useEffect(() => {
    const successMessage = sessionStorage.getItem('userAddedSuccess');
    if (successMessage) {
      dispatch(
        setSnackbar({
          open: true,
          message: successMessage,
          error: false,
        })
      );
      sessionStorage.removeItem('userAddedSuccess');
    }
  }, [dispatch]);

  useEffect(() => {
    setIsLoading(true);

    const promises = [
      getData(REGIONS_URL),
      getData(GET_CATEGORIES_URL),
      getData(GET_USER_BY_ROLE, { roleName: APPROVER_NAME }),
      getData(GET_USER_BY_ROLE, { roleName: RD_MANAGER_NAME }),
      getData(GET_USER_BY_ROLE, { roleName: REGULATORY_NAME }),
    ];

    Promise.all(promises)
      .then(([regions, categories, approvers, rdManagers, regulatoryUsers]) => {
        setRegionOptions(regions);
        setAllRegions(regions); // Store all regions
        setRegionNameOptions([defaultRegionOption, ...regions]);
        setAllCategories(categories); // Store all categories
        setCategoryNameOptions([defaultOption, ...categories]);
        setInitialCategoriesLoaded(true);

        // Combine APPROVER, RD_MANAGER, and REGULATORY users as potential managers
        const allManagerUsers = [...(approvers || []), ...(rdManagers || []), ...(regulatoryUsers || [])];
        const updatedManagers = allManagerUsers.map((manager) => ({
          id: manager.id,
          name: manager.email,
        }));
        setManagerNameOptions(updatedManagers);

        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, []);

  const roleOptions = [
    { id: "Select Role", name: "Select Role" },
    { id: "REGULATORY", name: "Regulatory" },
    { id: "R&D", name: "R&D" },
    { id: "MARKETING", name: "Marketing Team" },
    { id: "SUPPLY_CHAIN", name: "Supply Chain Team" },
    { id: "SUPPORT_PROVIDER", name: "Support Provider" },
    { id: "APPROVER", name: "Approver" },
    { id: "RD_MANAGER", name: "R&D Manager" },
    { id: "RD_COORDINATOR", name: "R&D Coordinator" },
  ];

  const [errors, setErrors] = useState({});

  // Handle edit mode data population - reset when not in edit mode
  useEffect(() => {
    if (editMode && editItem && initialCategoriesLoaded) {
      console.log("Edit mode activated with item:", editItem);

      setnewUserData({
        username: editItem.username,
        email: editItem.email,
        roles: editItem.role,
        regionId: editItem.regionId,
        managerId: editItem.managerId,
      });

      // Handle categories properly - check if it's string format from the listing
      let validCategories = [];
      if (editItem.categories) {
        if (typeof editItem.categories === 'string') {
          // Categories come as string with separator from the listing
          const categoryNames = editItem.categories.split('\u001F').filter(name => name.trim());
          // Find matching category objects from allCategories
          validCategories = categoryNames.map(name => {
            return allCategories.find(cat => cat.name?.trim().toUpperCase() === name.trim().toUpperCase());
          }).filter(Boolean); // Remove any undefined results
        } else if (Array.isArray(editItem.categories)) {
          // Categories come as array (original format)
          validCategories = editItem.categories.filter(cat => cat && cat.id && cat.name);
        }
        console.log("Valid categories found:", validCategories);
      }

      // Handle regions properly - check if it's string format from the listing
      let validRegions = [];
      if (editItem.regions) {
        if (typeof editItem.regions === 'string') {
          // Regions come as string with separator from the listing
          const regionNames = editItem.regions.split('\u001F').filter(name => name.trim());
          // Find matching region objects from allRegions
          validRegions = regionNames.map(name => {
            return allRegions.find(reg => reg.name?.trim().toUpperCase() === name.trim().toUpperCase());
          }).filter(Boolean); // Remove any undefined results
        } else if (Array.isArray(editItem.regions)) {
          // Regions come as array (original format)
          validRegions = editItem.regions.filter(reg => reg && reg.id && reg.name);
        }
        console.log("Valid regions found:", validRegions);
      }

      // Set the user categories and regions immediately
      setUserCategory(validCategories);
      setUserRegions(validRegions);

      // Update available category options by removing selected ones
      if (validCategories.length > 0) {
        const selectedCategoryIds = validCategories.map(cat => cat.id);
        console.log("Selected category IDs:", selectedCategoryIds);

        // Filter out selected categories from all available categories
        const availableCategories = allCategories.filter(cat => !selectedCategoryIds.includes(cat.id));
        setCategoryNameOptions([defaultOption, ...availableCategories]);
      } else {
        // If no categories selected, show all available categories
        setCategoryNameOptions([defaultOption, ...allCategories]);
      }

      // Update available region options by removing selected ones (for Approver/RD Manager role)
      if (validRegions.length > 0 && (editItem.role === "APPROVER" || editItem.role === "RD_MANAGER")) {
        const selectedRegionIds = validRegions.map(reg => reg.id);
        console.log("Selected region IDs:", selectedRegionIds);

        // Filter out selected regions from all available regions
        const availableRegions = allRegions.filter(reg => !selectedRegionIds.includes(reg.id));
        setRegionNameOptions([defaultRegionOption, ...availableRegions]);
      } else {
        // If no regions selected or not Manager role, show all available regions
        setRegionNameOptions([defaultRegionOption, ...allRegions]);
      }

      // Force form re-render
      setFormKey(prev => prev + 1);
    } else if (!editMode && initialCategoriesLoaded) {
      // Reset form when not in edit mode
      setnewUserData(initialState);
      setUserCategory([]);
      setUserRegions([]);
      // Reset category and region options to include all
      setCategoryNameOptions([defaultOption, ...allCategories]);
      setRegionNameOptions([defaultRegionOption, ...allRegions]);
      setFormKey(prev => prev + 1);
    }
  }, [editMode, editItem, initialCategoriesLoaded, allCategories, allRegions]);

  // Remove the additional effect as it's not needed
  // ...existing code...

  // Handle form submission for both add and edit modes
  const handleOnSubmit = async (isEditMode = false, editData = null) => {
    let allcategoryIds = [];
    if (newUserData.roles === "R&D" || newUserData.roles === "RD_MANAGER") {
      allcategoryIds = userCategories.map((category) => category.id);
    }

    let allRegionIds = [];
    if (newUserData.roles === "APPROVER" || newUserData.roles === "RD_MANAGER") {
      console.log("User regions in Approver/RD Manager role:", userRegions);
      allRegionIds = userRegions.map((region) => region.id);
    } else if (newUserData.roles === "RD_COORDINATOR") {
      // RD_COORDINATOR is system-wide — send no region IDs
      allRegionIds = [];
    } else {
      // For non-Manager roles, use single regionId
      if (newUserData.regionId) {
        allRegionIds = [newUserData.regionId];
      }
    }

    // Validation for both add and edit modes
    if ((newUserData.roles === "R&D" || newUserData.roles === "RD_MANAGER") && allcategoryIds.length === 0) {
      setErrors({
        ...errors,
        categoryIds: "At least one category must be selected",
      });
      return;
    }

    if ((newUserData.roles === "APPROVER" || newUserData.roles === "RD_MANAGER") && allRegionIds.length === 0) {
      console.log("In Approver/RD Manager role, userRegions:", userRegions);
      setErrors({
        ...errors,
        regionIds: "At least one region must be selected",
      });
      return;
    }

    if (isEditMode) {
      // For edit mode, send only editable fields
      const updateUserData = {
        username: editData.username, // Required but not editable
        regionId: (newUserData.roles === "APPROVER" || newUserData.roles === "RD_MANAGER" || newUserData.roles === "RD_COORDINATOR") ? null : newUserData.regionId,
        managerId: newUserData.managerId,
        categoryIds: allcategoryIds,
        regionIds: allRegionIds, // empty [] for RD_COORDINATOR
      };
      return updateUserData;
    } else {
      // For add mode, send all required fields
      const updatedUserData = {
        ...newUserData,
        roles: [newUserData.roles],
        categoryIds: allcategoryIds,
        regionIds: allRegionIds,
        // RD_COORDINATOR is system-wide — no region needed; set regionId to -1 so backend ignores it
        regionId: newUserData.roles === "RD_COORDINATOR" ? -1 : (newUserData.regionId || -1),
      };

      return submitFormWithValidation(
        updatedUserData,
        false,
        null,
        setErrors,
        getDynamicValidationSchema(newUserData.roles)
      );
    }
  };

  const clearData = () => {
    setnewUserData(initialState);
    setUserCategory([]);
    setUserRegions([]);
    setErrors({});
  };

  const handleOnBlur = (field, value) => {
    validateField(
      field,
      value,
      setnewUserData,
      newUserData,
      validationSchema,
      setErrors
    );
  };

  const form = (formEditMode = false, formEditItem = null) => {
    return (
      <form key={formKey} onSubmit={handleOnSubmit} className="formWrapper">
        <Grid
          className="inputContainer"
          container
          spacing={2}
          justifyContent="flex-end"
        >
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Username
            </Typography>
            <TextField
              onBlur={(e) => handleOnBlur("username", e.target.value)}
              value={newUserData.username || ""}
              onChange={(e) => {
                setnewUserData({
                  ...newUserData,
                  username: e.target.value,
                });
                setErrors({ ...errors, username: "" });
              }}
              variant="outlined"
              placeholder=" User Name"
              fullWidth
              disabled={formEditMode}
              helperText={errors.username}
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Email
            </Typography>
            <TextField
              onBlur={(e) => {
                handleOnBlur("email", e.target.value);
              }}
              value={newUserData.email || ""}
              onChange={(e) => {
                setnewUserData({ ...newUserData, email: e.target.value });
                setErrors({ ...errors, email: "" });
              }}
              variant="outlined"
              placeholder="Email"
              fullWidth
              disabled={formEditMode}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Role
            </Typography>
            <DropDown
              state={newUserData.roles}
              setState={(value) => {
                setnewUserData({
                  ...newUserData,
                  roles: value ? value : null,
                });
                setErrors({ ...errors, roles: "" });
              }}
              name="role"
              label=""
              items={roleOptions}
              placeholder="Select Role"
              helperText={errors.roles}
              disabled={formEditMode}
            />
          </Grid>

          <Grid item xs={6}>
            {/* Only show Region field for roles that are region-specific */}
            {newUserData.roles && newUserData.roles !== "Select Role" && newUserData.roles !== "RD_COORDINATOR" ? (
              <>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  {(newUserData.roles === "APPROVER" || newUserData.roles === "RD_MANAGER") ? "Regions" : "Region"}
                </Typography>
                {/* Show single region dropdown for non-Manager roles */}
                {newUserData.roles !== "APPROVER" && newUserData.roles !== "RD_MANAGER" ? (
                  <SearchableDropDown
                    state={newUserData.regionId}
                    setState={(value) => {
                      setnewUserData({
                        ...newUserData,
                        regionId: value ? value : null,
                      });
                      setErrors({ ...errors, regionId: "" });
                    }}
                    name="regionName"
                    label=""
                    items={regionOptions}
                    placeholder="Select Region Name"
                    helperText={errors.regionId}
                    disabled={false}
                  />
                ) : (
                  /* Show multiple region selection for Manager role */
                  <>
                    <DropDown
                      state="Select Region Name"
                      setState={(value) => {
                        const selectedRegion = regionNameOptions.find((option) => option.id === value);
                        if (selectedRegion && selectedRegion.id !== "Select Region Name") {
                          setUserRegions([...userRegions, selectedRegion]);
                          setRegionNameOptions(
                            regionNameOptions.filter((option) => option.id !== value)
                          );
                          setErrors({ ...errors, regionIds: "" });
                        }
                      }}
                      name="regionName"
                      label=""
                      items={regionNameOptions}
                      placeholder="Select Region Name"
                      helperText={errors.regionIds}
                      disabled={false}
                    />
                    {/* Regions chips display for Manager role - directly under dropdown */}
                    {userRegions.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          alignItems: "flex-start",
                          justifyContent: "flex-start",
                          mt: 1,
                          gap: 1,
                        }}
                      >
                        {userRegions.filter(reg => reg && reg.name).map((value, idx) => (
                          <Chip
                            key={`region-${value.id}-${idx}`}
                            label={value.name}
                            size="small"
                            sx={{
                              fontWeight: 520,
                              mr: 1,
                              "& .MuiChip-icon": {
                                color: red[600],
                              },
                              cursor: "pointer",
                            }}
                            onClick={() => {
                              setUserRegions(
                                userRegions.filter((item) => item !== value)
                              );
                              setRegionNameOptions([...regionNameOptions, value]);
                            }}
                            icon={<CancelIcon />}
                          />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </>
            ) : newUserData.roles === "RD_COORDINATOR" ? (
              /* RD_COORDINATOR is system-wide — no region needed */
              <Box
                sx={{
                  mt: 3.5,
                  p: 1.5,
                  borderRadius: 1,
                  backgroundColor: "primary.50",
                  border: "1px dashed",
                  borderColor: "primary.light",
                }}
              >
                <Typography variant="caption" color="primary.main" fontWeight={600}>
                  🌐 System-wide role — not tied to any region
                </Typography>
              </Box>
            ) : (
              /* Show disabled field when no role is selected */
              <>
                <Typography variant="body1" sx={{ mb: 1 }}>Region</Typography>
                <SearchableDropDown
                  state=""
                  setState={() => { }}
                  name="regionName"
                  label=""
                  items={[]}
                  placeholder="Select Role First"
                  helperText=""
                  disabled={true}
                />
              </>
            )}
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              Manager( Optional )
            </Typography>
            <SearchableDropDown
              state={newUserData.managerId}
              setState={(value) => {
                setnewUserData({
                  ...newUserData,
                  managerId: value ? value : null,
                });
                setErrors({ ...errors, managerId: "" });
              }}
              name="ManagerName"
              label=""
              items={managerNameOptions}
              placeholder="Select Manager Name"
              helperText={errors.managerId}
              disabled={false}
            />
          </Grid>
          {/* Category field — only shown for R&D and RD_MANAGER; hidden for RD_COORDINATOR */}
          {(newUserData.roles === "R&D" || newUserData.roles === "RD_MANAGER") && (
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Category Name
              </Typography>
              <DropDown
                state="Select Category Name"
                setState={(value) => {
                  const selectedCategory = categoryNameOptions.find((option) => option.id === value);
                  if (selectedCategory && selectedCategory.id !== "Select Category Name") {
                    setUserCategory([...userCategories, selectedCategory]);
                    setCategoryNameOptions(
                      categoryNameOptions.filter((option) => option.id !== value)
                    );
                    setErrors({ ...errors, categoryIds: "" });
                  }
                }}
                name="categoryName"
                label=""
                items={categoryNameOptions}
                placeholder="Select Category Name"
                helperText={errors.categoryIds}
                disabled={false}
              />
            </Grid>
          )}

          {/* Categories chips display */}
          {userCategories.length > 0 && (
            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "flex-start",
                  justifyContent: "flex-start",
                  mt: 1,
                  gap: 1,
                }}
              >
                {userCategories.filter(cat => cat && cat.name).map((value, idx) => (
                  <Chip
                    key={`category-${value.id}-${idx}`}
                    label={value.name}
                    size="small"
                    sx={{
                      fontWeight: 520,
                      mr: 1,
                      "& .MuiChip-icon": {
                        color: red[600],
                      },
                      cursor: "pointer",
                    }}
                    onClick={() => {
                      setUserCategory(
                        userCategories.filter((item) => item !== value)
                      );
                      setCategoryNameOptions([...categoryNameOptions, value]);
                    }}
                    icon={<CancelIcon />}
                  />
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </form>
    );
  };

  const initialColumnVisibilityModel = {
    managerEmail: false,
  }

  const regionFilterComponent = (
    <RegionFilter
      regions={regionOptions}
      selectedRegion={selectedRegionFilter}
      onRegionChange={setSelectedRegionFilter}
      persistState={true}
      label="Filter by Region"
    />
  );

  return (
    <GenericItemsListing
      items_URL={USERS_URL}
      SAVE_Item={USER_REGISTER_URL}
      DELETE_Item={DELETE_USER}
      title="Users"
      name="User"
      icon={<PersonRoundedIcon fontSize="large" color="white" />}
      attributes={attributes}
      customFormForAddtion={{ formContent: form, formButton: handleOnSubmit }}
      uniqueId={"id"}
      clearData={clearData}
      listLabel={[
        { listName: "categories", listAttr: "name" },
        { listName: "regions", listAttr: "name" }
      ]}
      initialColumnVisibilityModel={initialColumnVisibilityModel}
      editable={true}
      // filterComponent={
      // Pass edit state to GenericItemsListing
      editMode={editMode}
      setEditMode={setEditMode}
      editItem={editItem}
      setEditItem={setEditItem}
    />
  );
}

export default UsersListing;
